import React, { useState, useEffect, useRef } from "react";
import {
  Grid, Typography, Button, TextField, Box, Chip,
} from "@mui/material";
import { generator, rotatearray, Alphacode } from "../utils/euclidean";
import RhythmPlayback from "./audiocomponents/RhythmicPlayback";
import AlphariddimsVisualizerCircle from "./visualcomponents/AlphariddimsVisualizerCircle";
import useMediaQuery from "@mui/material/useMediaQuery";
import Alphariddims from "../data/alphariddims";

const High_Conga = "https://raw.githubusercontent.com/urbangriot/Drive/refs/heads/main/griotquest/audio/Conga/SO_AFR_conga_high.mp3";
const Slap_Conga = "https://raw.githubusercontent.com/urbangriot/Drive/refs/heads/main/griotquest/audio/Conga/SO_AFR_conga_slap_closed.mp3";
const Low_Conga  = "https://raw.githubusercontent.com/urbangriot/Drive/refs/heads/main/griotquest/audio/Conga/SO_AFR_conga_low.mp3";

// ── helpers ────────────────────────────────────────────────────────────────

// Strip anything that isn't a plain A–Z letter (no spaces, no punctuation),
// then uppercase, so the input can only ever contain valid Alphariddim keys.
const sanitiseNameInput = (text) => text.replace(/[^a-zA-Z]/g, "").toUpperCase();

// ── component ──────────────────────────────────────────────────────────────
const AlphariddimsSequencerButtons = () => {
  // shared playback state
  const [sequence,    setSequence]    = useState([]);
  const [isPlaying,   setIsPlaying]   = useState(false);
  const [bpm,         setBpm]         = useState(120);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [loop,        setLoop]        = useState(false);
  const [label,       setLabel]       = useState("A");   // shown in visualizer
  const currentBeatRef = useRef(0);
  const isDesktop = useMediaQuery("(min-width:900px)");

  // ── single letter (button grid) ──────────────────────────────────────────
  const [selectedLetter, setSelectedLetter] = useState("A");

  const loadLetter = (key) => {
    const entry = Alphariddims[key];
    if (!entry) return;
    const seq = Alphacode(
      rotatearray(generator(entry.pattern[0], entry.pattern[1]), entry.offset),
      entry.alphariddim
    );
    setSequence(seq);
    setBpm(entry.bpm);
    setLabel(key);
  };

  const handleLetterClick = (key) => {
    // typing in the name field and clicking a letter both drive the same
    // single-letter playback, so clear any in-progress name playback
    setIsPlayingName(false);
    setSelectedLetter(key);
    loadLetter(key);
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), 100);
  };

  // ── name / word spelled out letter by letter ─────────────────────────────
  const [nameInput,      setNameInput]      = useState("");
  const [isPlayingName,  setIsPlayingName]  = useState(false);
  const [letterIndex,    setLetterIndex]    = useState(0);

  const handleNameInputChange = (e) => {
    setNameInput(sanitiseNameInput(e.target.value));
  };

  const handlePlayName = async () => {
    if (window.audioContext?.state === "suspended") await window.audioContext.resume();
    if (nameInput.length === 0) return;
    if (isPlayingName) {
      console.log("[Alphariddims] Stop pressed");
      setIsPlayingName(false);
      setIsPlaying(false);
      return;
    }
    console.log("[Alphariddims] Play pressed for:", nameInput);
    setIsPlaying(false);
    setLetterIndex(0);
    setIsPlayingName(true);
  };

  // load the current letter of the name whenever name-playback is active
  // and advances. RhythmPlayback drives isPlaying transitions for letters
  // after the first one (via setIsPlaying passed into it); this effect only
  // needs to kick off playback for letter 0.
  useEffect(() => {
    if (!isPlayingName) return;
    if (letterIndex >= nameInput.length) {
      console.log("[Alphariddims] letterIndex", letterIndex, ">= length", nameInput.length, "-> done");
      setIsPlayingName(false);
      setIsPlaying(false);
      return;
    }
    const key = nameInput[letterIndex];
    console.log("[Alphariddims] loading letter", letterIndex, "=", key);
    loadLetter(key);
    currentBeatRef.current = 0;
    if (letterIndex === 0) setIsPlaying(true);
  }, [isPlayingName, letterIndex]);

  const advanceNameLetter = () => {
    console.log("[Alphariddims] advanceNameLetter called, current letterIndex (closure):", letterIndex, "nameInput.length:", nameInput.length, "loop:", loop);
    if (letterIndex < nameInput.length - 1) {
      setLetterIndex((prev) => {
        console.log("[Alphariddims] setLetterIndex: prev =", prev, "-> next =", prev + 1);
        return prev + 1;
      });
      return true;
    }
    if (loop) {
      console.log("[Alphariddims] advanceNameLetter: reached end, loop is ON -> restarting from letter 0");
      setLetterIndex(0);
      return true;
    }
    console.log("[Alphariddims] advanceNameLetter: reached end, loop is OFF -> stopping");
    setIsPlayingName(false);
    setIsPlaying(false);
    return false;
  };

  // ── playback controls ─────────────────────────────────────────────────────
  // letters mode: regenerate on play toggle (single-letter button playback)
  useEffect(() => {
    if (isPlayingName || !isPlaying) return;
    loadLetter(selectedLetter);
    currentBeatRef.current = 0;
  }, [isPlaying]);

  // ── initial mount ─────────────────────────────────────────────────────────
  useEffect(() => {
    loadLetter("A");
  }, []);

  return (
    <Grid container spacing={2} alignItems="flex-start" width="100%">

      {/* Title */}
      <Grid item xs={12}>
        <Typography sx={{ color: "#ffffff" }} variant="h2">Alphariddims</Typography>
      </Grid>

      {/* ── A–Z LETTERS ── */}
      <Grid item xs={12} md={10} sx={{ ml: "4%" }}>
        {Object.keys(Alphariddims).map((key) => (
          <Button
            key={key}
            variant={selectedLetter === key && !isPlayingName ? "contained" : "outlined"}
            onClick={() => handleLetterClick(key)}
            style={{ width: "20%", height: isDesktop ? "30%" : "50px" }}
          >
            {key}
          </Button>
        ))}
      </Grid>

      {/* ── Playback controls ── */}
      <Grid item xs={12} sx={{ ml: "4%", display: "flex", gap: 1 }}>
        <Button
          variant="contained"
          color={isPlaying ? "secondary" : "primary"}
          onClick={() => setLoop((prev) => !prev)}
        >
          {loop ? "Loop" : "OneShot"}
        </Button>

        <Button
          variant="outlined"
          color="error"
          disabled={!isPlaying && !isPlayingName}
          onClick={() => {
            setIsPlaying(false);
            setIsPlayingName(false);
          }}
        >
          Stop
        </Button>
      </Grid>

      {/* ── Visualizer ── */}
      <Grid item xs={12} sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <AlphariddimsVisualizerCircle
          sequence={sequence}
          letter={label}
          currentBeat={currentBeat}
        />
      </Grid>

      {/* ── Name / word spell-out input ── */}
      <Grid item xs={12} sx={{ px: 2 }}>
        <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", mb: 1 }}>
          Enter a name or word. It will be played back letter by letter using each letter's Alphariddim pattern.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField
            fullWidth
            variant="outlined"
            label="Name or word"
            placeholder="e.g. Pierre"
            value={nameInput}
            onChange={handleNameInputChange}
            InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)" } }}
            inputProps={{ style: { color: "#fff" } }}
            sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.25)" } } }}
          />
          <Button
            variant="contained"
            color={isPlayingName ? "secondary" : "primary"}
            disabled={nameInput.length === 0}
            onClick={handlePlayName}
            sx={{ height: "56px", whiteSpace: "nowrap" }}
          >
            {isPlayingName ? "Stop" : "Play"}
          </Button>
        </Box>

        {/* Letter chips showing progress through the name */}
        {nameInput.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
            {nameInput.split("").map((ch, i) => (
              <Chip
                key={i}
                label={ch}
                size="small"
                sx={{
                  bgcolor: i === letterIndex && isPlayingName ? "#F5A623" : "rgba(255,255,255,0.1)",
                  color:   i === letterIndex && isPlayingName ? "#1a1a2e"  : "#fff",
                  fontWeight: 700,
                }}
              />
            ))}
          </Box>
        )}
      </Grid>

      {/* ── RhythmPlayback ── */}
      <RhythmPlayback
        sequence={sequence}
        bpm={bpm}
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        onCurrentBeatChange={(beat) => { setCurrentBeat(beat); currentBeatRef.current = beat; }}
        reset_playback={isPlayingName ? advanceNameLetter : () => setIsPlaying(false)}
        sampleUrls={[High_Conga, Slap_Conga, Low_Conga]}
        loop={isPlayingName ? true : loop}
        isPlaylist={isPlayingName}
        nextelement={isPlayingName ? advanceNameLetter : undefined}
      />
    </Grid>
  );
};

export default AlphariddimsSequencerButtons;